import React from 'react';

import type {HeadingProps} from '@tempo/core';
import {Heading} from '@tempo/core';
import {getVW} from '@modules/layout/fluidSizing';

type CustomHeadingContext =
	| 'Chapter'
	| 'QuizSliderPercentage'
	| 'FlipCardName'
	| 'FlipCardText'
	| 'PageNumber'
	| 'PageTitle'
	| 'TopTitle'
	| 'TopElementNumber'
	| 'TopElementTitle'
	| 'CoverTitle'
	| 'PagePoster'
	| 'RecapReceiverPoints'
	| 'SendReceiverTitle';

const CustomHeading = ({
	children,
	customContext,
	...rest
}: Partial<HeadingProps> & {customContext: CustomHeadingContext}) => {
	let otherProps: Partial<HeadingProps> = {};

	switch (customContext) {
		case 'Chapter':
			otherProps = {
				variant: 'heading.xl',
				fontFamily: 'Deezer Product, Inter',
				fontSize: 'clamp(1.5em, 5vh, 6em)',
				lineHeight: 1.2,
			};
			break;

		case 'QuizSliderPercentage':
			otherProps = {
				textAlign: 'center',
				variant: 'display.l',
				fontSize: '120px',
				lineHeight: '88px',
				fontWeight: '800',
				style: {fontVariantNumeric: 'tabular-nums'},
			};
			break;

		case 'FlipCardName':
			otherProps = {
				variant: 'heading.xl',
				fontFamily: 'Deezer Product, Inter',
				style: {
					fontSize: 'clamp(1em, 4vh, 6em)',
					lineHeight: 'clamp(1.15em, 6vh, 7em)',
				},
			};
			break;

		case 'FlipCardText':
			otherProps = {
				fontWeight: 400,
				fontFamily: 'Deezer Product, Inter',
				style: {
					fontSize: 'clamp(1em, 4vh, 5em)',
					lineHeight: 'clamp(1em, 4vh, 5em)',
				},
				padding: '2vh',
			};
			break;

		case 'PageNumber':
			otherProps = {
				style: {
					fontSize: 'clamp(1em, 12vh, 15em)',
					lineHeight: 1,
				},
				fontWeight: '700',
				fontFamily: 'Deezer Numerical Alternate Narrow',
			};
			break;

		case 'PageTitle':
			otherProps = {
				variant: {base: 'heading.l'},
				fontFamily: 'Deezer Product, Inter',
				style: {
					fontSize: 'clamp(1em, 5vh, 5em)',
					lineHeight: 1,
				},
			};
			break;

		case 'TopTitle':
			otherProps = {
				variant: 'heading.l',
				fontFamily: 'Deezer Product, Inter',
				style: {
					fontSize: 'clamp(1em, 4vh, 3em)',
					lineHeight: 1.5,
				},
			};
			break;

		case 'TopElementNumber':
			otherProps = {
				variant: 'heading.l',
				style: {
					fontSize: 'clamp(1em, 4vh, 3em)',
					lineHeight: 1.5,
				},
				fontFamily: 'Deezer Numerical Alternate Narrow',
			};
			break;

		case 'TopElementTitle':
			otherProps = {
				variant: 'heading.s',
				fontFamily: 'Deezer Product, Inter',
				style: {
					fontSize: 'clamp(1em, 1.6vh, 3em)',
					lineHeight: 1.5,
				},
			};
			break;

		case 'CoverTitle':
			otherProps = {
				fontFamily: 'Deezer Product, Inter',
				textColor: '#000000',
				textTransform: 'uppercase',
				variant: 'heading.l',
				style: {
					fontSize: 'clamp(1em, 4vh, 3em)',
					lineHeight: 1,
				},
			};
			break;

		case 'PagePoster':
			otherProps = {
				variant: 'heading.l',
				fontFamily: 'Deezer Product, Inter',
				style: {
					fontSize: getVW(32),
					lineHeight: 1,
				},
			};
			break;
		case 'RecapReceiverPoints':
			otherProps = {
				fontFamily: 'Deezer Product, Inter',
				fontWeight: 800,
				fontSize: 80,
			};
			break;
		case 'SendReceiverTitle':
			otherProps = {
				variant: 'heading.l',
				fontFamily: 'Deezer Product, Inter',
				textTransform: 'uppercase',
				style: {
					fontSize: 'clamp(1em, 5vh, 6em)',
					lineHeight: 1,
				},
			};
			break;
	}

	return (
		<Heading {...otherProps} {...rest}>
			{children}
		</Heading>
	);
};

export default CustomHeading;
